<template lang="pug">
  .custom-bumper
    ProductPage(:product="product" :showTabsSlot="true" :showMainSlot="true")
      template(#media)
        Media(
          :gallery="gallery"
          zoomed="none"
          v-if="config"
        )
          template(#thumbs)
            .custom-bumper-thumb.thumbs-gallery(
              v-for="(plate, index) in gallery"
              :class="focusedThumbnail === index && 'active'"
              @click="focusedThumbnail = index"
            )
              custom-bumper-render(
                render-type="2d"
                :config="config"
                :staticImage="plate.src"
                :canvas-image="canvases[index]"
                :weight="plate.weight"
                :showRogueLogo="showRogueLogo"
                :ref="el => setRenderer(plate.weight, el)"
                :id="'gallery-'+plate.weight"
                :sku="product.sku"
              )
          template(#default)
            custom-bumper-render(
              render-type="2d"
              :config="config"
              :static-image="gallery[focusedThumbnail].src"
              :canvas-image="canvases[focusedThumbnail]"
              :weight="gallery[focusedThumbnail].weight"
              :showRogueLogo="showRogueLogo"
              :weightsAdded="weightsAdded"
              id="main"
              :sku="product.sku"
            )
      template(#title="slotProps")
          ProductTitle(:product="product" :show-h1="slotProps.showH1")
      template(#tabs)
        RenderBlock(:block="slots.tabs" :product="product")
      template(#rhpa)
        .rhpa
          Message.error(status="error" v-if="!!error")
            Icon.left-icon(name="alert-error")
            | {{ __(error) }}
          RenderBlock(v-if="slots.aboveRhpa" :block="slots.aboveRhpa")
          ProductBadges.rhpa-badges( :buyRequest="buyRequest" )
          .opts
            .opt-group
              .opt-group-head(@click.stop.prevent="showDesign = !showDesign")
                .header
                  .content
                    Icon.header-icon.zeus(name="misc/zeus-head")
                    h5 {{__('Add Custom Design')}}
                  Icon(:name="showDesign ? 'arrow-up' : 'arrow-down'" fill="gray_dark")
              client-only
                keep-alive
                  custom-bumper-options(
                    v-if="showDesign && config"
                    v-model="config"
                    :sku="product.sku"
                    @toggle-rogue-logo="showRogueLogo = $event")
            .opt-group
              .opt-group-head(@click.stop.prevent="showWeights = !showWeights")
                .header
                  .content
                    Icon.header-icon(name="misc/weight-variant" fill="inherit")
                    h5 {{__('Pick Weight Variants')}}
                Icon(:name="showWeights ? 'arrow-up' : 'arrow-down'" fill="gray_dark")
              .opt-group-body(v-if="showWeights")
                rhpa-body(:buyRequest="buyRequest")
          rhpa-hundo.hundo(
            v-if="product.hundoPricing"
            :product="product"
            :buy-request="buyRequest"
            :minimumWeight=200
          )
          minimum-weight(v-else :minimum-weight="200" :added-weight="addedWeight" )
          rhpa-footer(:buy-request="buyRequest" :disabled="disableAddToCart" :validate="createRecipe")
      template(#flexible)
        RenderBlock(:block="slots.flexible" :product="product")
      template(#main)
        RenderBlock(:block="slots.main" :product="product")
      template(#full)
        RenderBlock(:block="slots.full" :product="product")
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProductStore } from '@/js/stores/productStore'
import RhpaHundo from '@/js/components/rhpa/rhpa-hundo.vue'
import CustomBumperRender from '@/js/pages/product/custom/custom-bumper-render.vue'
import CustomBumperOptions from '@/js/pages/product/custom/bumper-options.vue'
import MinimumWeight from '@/js/pages/product/custom/minimum-weight.vue'
import { mapRecipeToConfig, mapConfigToRecipe, hashCode } from '@/js/pages/product/custom/utils'
import type { CustomBumperConfig, CustomBumperDesign, Customization, Recipe } from '@/js/pages/product/custom/types'
import { CUSTOM_BUMPER_DATA, PRINTER_COLORS } from '@/js/pages/product/custom/constants'
import { arrayDeepEquals } from '@/js/services/Utils'
import { validateBadWords } from '@/js/helpers/validations/validators'
import type { BuyRequest } from '@/js/services/RogueTypes'
import { useRogue } from '@/js/services/Rogue'

const RhpaBody = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-body.vue'))
const ProductTitle = defineAsyncComponent(() => import('../../../components/rhpa/product-title.vue'))
const RhpaFooter = defineAsyncComponent(() => import('../../../components/rhpa/rhpa-footer.vue'))
const ProductBadges = defineAsyncComponent(() => import('../../../components/rhpa/badges.vue'))

const MIN_WEIGHT = 200
const WEIGHT_MAP: { [weight: number]: number } = {
  110 : 55,
  90  : 45,
  70  : 35,
  50  : 25,
  30  : 15,
  20  : 10,
}

// Kept across instances so switching bumper types doesn't recreate recipes we already have
const previousCustomizations: { [productId: number]: Customization[] } = {}
const recipeIds: { [productId: number]: string } = {}
const recipeHashs: { [productId: number]: number } = {}

const props = defineProps<{
  buyRequest: BuyRequest,
}>()

const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const productStore = useProductStore()

const showDesign = ref<boolean>(true)
const showWeights = ref<boolean>(false)
const showRogueLogo = ref<boolean>(false)
const focusedThumbnail = ref<number>(0)
const error = ref<string | null>(null)

// Set when an NFL recipe sends us to /rogue-nfl-bumpers. Not reactive - only read imperatively
let redirecting = false

const config = ref<CustomBumperDesign>({
  font     : 'arvo',
  fontSize : '250',
  color    : 'white',
  text     : '',
  design   : null,
  image    : null,
  file     : null,
  type     : 'text',
})

// The thumbnail renderer for each plate weight - createRecipe pulls the render/stencil off these.
// Deliberately not reactive: only read imperatively, and it is written from a template ref during patch
const renderers: { [weight: number]: InstanceType<typeof CustomBumperRender> } = {}

function setRenderer(weight: number, el: InstanceType<typeof CustomBumperRender> | null) {
  if (el) {
    renderers[weight] = el
  } else {
    delete renderers[weight]
  }
}

const products = computed(() => productStore.products)
const product = computed(() => products.value[props.buyRequest.productId])
const slots = computed(() => ((product.value.roguebuilder || {}).slots || {}))
const gallery = computed(() => CUSTOM_BUMPER_DATA[product.value.sku!]?.galleryImages)
const canvases = computed(() => CUSTOM_BUMPER_DATA[product.value.sku!].canvasImages)
const recipeId = computed(() => route.query.rid as string | undefined)

const addedWeight = computed(() => {
  // get children with quantity and their amount
  // then get their weights from the products
  const children = props.buyRequest.children!.filter(c => c.qty).reduce((a, v) => ({
    ...a,
    [v.productId]: v.qty,
  }), {} as { [productId: string]: number })
  let weight = 0
  Object.keys(children).forEach(productId => weight += products.value[productId].weight! * children[productId])

  return weight > MIN_WEIGHT ? MIN_WEIGHT : weight
})

const disableAddToCart = computed(() => addedWeight.value < MIN_WEIGHT)

const weightsAdded = computed(() => props.buyRequest.children!
  .filter(child => !!child.qty && !!WEIGHT_MAP[products.value[child.productId].weight!])
  .map(child => WEIGHT_MAP[products.value[child.productId].weight!]))

async function validate() {
  error.value = null
  error.value = (await validateBadWords(config.value.text)) ? 'Customization cannot contain bad or copy written language' : null

  return error.value
}

async function createRecipe(target: BuyRequest = props.buyRequest) {
  if (await validate()) {
    return false
  }

  const baseConfig: Omit<CustomBumperConfig, 'sku'> = {
    ...config.value,
    color     : PRINTER_COLORS[config.value.color],
    productId : product.value.productId,
  }

  await Promise.all(target.children!.map(async (c, i) => {
    if (!c.qty) {
      return
    }

    const childProduct = products.value[c.productId]
    const childConfig: CustomBumperConfig = { ...baseConfig, sku: childProduct.sku! }

    const { customizations } = mapConfigToRecipe(childConfig) || {}

    if (!customizations) {
      return
    }

    if (!(previousCustomizations[c.productId] && arrayDeepEquals(customizations, previousCustomizations[c.productId]))) {
      const template = renderers[WEIGHT_MAP[childProduct.weight!]]
      const { render, stencil } = await template.getRender()

      try {
        const recipe = await rogue.fetch('/svc_zeus/recipes', 'POST', mapConfigToRecipe({ ...childConfig, renders: [render], stencils: [stencil] }))
        recipeIds[c.productId] = recipe.recipeId
        recipeHashs[c.productId] = hashCode(JSON.stringify({ customizations }))
        previousCustomizations[c.productId] = customizations
      } catch (e) {
        error.value = 'There was an error creating your customization. Please try again later.'
        rogue.log.error('Error creating recipe', e as Error)

        return
      }
    }

    const child = target.children![i]
    child.metadata = child.metadata || {}
    child.metadata.recipeId = recipeIds[c.productId]
    child.metadata.recipeHash = recipeHashs[c.productId]
    child.metadata.nonFluidCustomization = true
  }))

  return !error.value
}

async function loadProducts() {
  await productStore.loadAllChildren(props.buyRequest.productId, rogue)
  focusedThumbnail.value = 0
  config.value.color = CUSTOM_BUMPER_DATA[product.value.sku!]?.colorOptions[0].value
}

async function loadRecipe() {
  // if recipe ID try to load recipe
  if (recipeId.value && !redirecting) {
    try {
      const recipe = await rogue.fetch<Recipe>(`/svc_zeus/recipes/${recipeId.value}`)
      Object.assign(config.value, mapRecipeToConfig(recipe))

      // Zeus does not return productId, so this is usually a no-op. Assigning it unguarded put
      // undefined on the root buy request, which threw out of the buy request meta watcher.
      if (recipe.productId) {
        props.buyRequest.productId = recipe.productId
      }
    } catch {
      rogue.log.warn('Failed fetching recipe', recipeId.value)
    }
  }
}

watch(() => props.buyRequest.productId, () => loadProducts())

watch(recipeId, () => loadRecipe())

onBeforeMount(() => {
  if (recipeId.value?.startsWith('NFL-')) {
    // This instance is on its way out - the NFL route loads the recipe itself
    redirecting = true
    router.replace({ path: '/rogue-nfl-bumpers', query: { rid: recipeId.value } })
  }
})

onServerPrefetch(() => loadProducts())

onMounted(() => {
  loadProducts()
  loadRecipe()
})
</script>
<style lang="scss" scoped>

.hundo {
  margin-top: $size-xxs
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  .content {
    display: flex;
    align-items: center;
  }
}
.header-icon {
  height: $size-xxl;
  width: $size-xxl;
  margin-right: $size-xxs;
}
.opt-group {
  &:first-child{
    border-top: 1px solid $color-gray;
  }
  border-bottom: 1px solid $color-gray;
}
.opt-group-head {
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  padding: $space-s 0;
  h5 {
    margin-bottom: 0;
  }
}
@include mobile(){
  .custom-bumper {
    overflow: hidden;
  }
}
.custom-bumper-thumb {
  width: 104px;
  border: 2px solid transparent;
  margin: 0 auto;
  &.active {
    border: 2px solid black;
    box-sizing: border-box;
  }
}

.error {
  margin: $space-s 0;
}
:deep() {
  .grid-rhpa {
    z-index: 10;
  }
}

</style>

<style lang="scss">
@include not-desktop() {
  .custom-bumper .thumbs {
    display: block !important;
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    position: absolute;
  }
}
</style>
